import { Eye, EyeOff, Loader2, Lock, Phone, Wifi } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { fetchData } from "../../services/apiService";

const PortalLogin: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // MikroTik redirect params (from your custom login.html)
  const linkLogin = searchParams.get("link_login");
  const linkLoginOnly = searchParams.get("link_login_only");
  const linkOrig = searchParams.get("link_orig");

  const routerId = searchParams.get("router_id");
  const uamip = searchParams.get("uamip") || "10.5.50.1";
  const mac = searchParams.get("mac");

  // Save MikroTik links once (so they persist across pages)
  useEffect(() => {
    if (linkLogin) sessionStorage.setItem("mt_link_login", linkLogin);
    if (linkLoginOnly)
      sessionStorage.setItem("mt_link_login_only", linkLoginOnly);
    if (linkOrig) sessionStorage.setItem("mt_link_orig", linkOrig);
    if (mac) sessionStorage.setItem("mt_mac", mac);
    if (uamip) sessionStorage.setItem("mt_uamip", uamip);
    if (routerId) sessionStorage.setItem("hs_router_id", routerId);
  }, [linkLogin, linkLoginOnly, linkOrig, mac, uamip, routerId]);

  // Build query string so Register/Login/Plans keep the MT links
  const queryStr =
    `?mac=${encodeURIComponent(mac || "")}` +
    `&uamip=${encodeURIComponent(uamip || "")}` +
    `&router_id=${encodeURIComponent(routerId || "")}` +
    `&link_login=${encodeURIComponent(
      linkLogin || sessionStorage.getItem("mt_link_login") || "",
    )}` +
    `&link_login_only=${encodeURIComponent(
      linkLoginOnly || sessionStorage.getItem("mt_link_login_only") || "",
    )}` +
    `&link_orig=${encodeURIComponent(
      linkOrig || sessionStorage.getItem("mt_link_orig") || "",
    )}`;

  const [formData, setFormData] = useState({ phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // ✅ FIXED: Removed the "+" prefix here
  const formatPhoneForDb = (phone: string) => {
    let p = phone.replace(/\D/g, "");
    if (p.startsWith("0")) p = p.substring(1);
    if (!p.startsWith("254")) p = "254" + p;
    return p; // Returns 254712345678
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const finalPhone = formatPhoneForDb(formData.phone);

      const res = await fetchData("/portal/login", {
        method: "POST",
        auth: "none",
        body: JSON.stringify({
          username: finalPhone,
          password: formData.password,
          mac: mac,
        }),
      });

      sessionStorage.setItem("hs_user", JSON.stringify(res));
      sessionStorage.setItem("hs_ip", uamip);
      if (routerId) sessionStorage.setItem("hs_router_id", routerId);

      navigate(`/portal/plans${queryStr}`);
    } catch (err: any) {
      console.error(err);
      setError("Invalid phone number or password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl shadow-lg mb-4">
            <Wifi className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome Back
          </h1>
          <p className="text-gray-600">
            Sign in to manage your internet connection
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label
                htmlFor="phone"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Phone Number
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Phone className="h-5 w-5 text-gray-400" />
                </div>
                <div className="absolute inset-y-0 left-11 flex items-center pointer-events-none">
                  <span className="text-gray-500 font-medium">254</span>
                </div>
                <input
                  type="tel"
                  name="phone"
                  id="phone"
                  className="block w-full pl-20 pr-4 py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-gray-900 placeholder-gray-400"
                  placeholder="712 345 678"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  required
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label
                  htmlFor="password"
                  className="block text-sm font-semibold text-gray-700"
                >
                  Password
                </label>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  id="password"
                  className="block w-full pl-12 pr-12 py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-gray-900 placeholder-gray-400"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <p className="text-sm text-red-800 font-medium">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-3.5 px-4 rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5" />
                  Logging in...
                </span>
              ) : (
                "Log In"
              )}
            </button>
          </form>

          <div className="text-center mt-6">
            <p className="text-sm text-gray-600">
              New here?{" "}
              <Link
                to={`/portal/register${queryStr}`}
                className="font-semibold text-blue-600 hover:text-blue-700 transition-colors"
              >
                Create an account
              </Link>
            </p>
          </div>
        </div>

        <p className="text-center mt-6 text-sm text-gray-500">
          By signing in, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
};

export default PortalLogin;
